import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { CloseIcon } from 'assets/icons/CloseIcon'
import { CursorPointer, Flex } from 'styling/GlobalStyles'
import { Button } from 'components/ui/Button/Button'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { HonestSelectInput } from 'components/HonestSelect/HonestSelectInput'
import { CheckChip } from 'components/CheckBox/CheckChip'
import { Input } from 'components/Input/Input'
import { Modal } from 'components/Modal/Modal'
import CalendarInput from 'components/CalendarInput/CalendarInput'
import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { AppDispatch, RootState } from 'store'
import {
  createFoodMealRequest,
  decideFoodMealRequest,
  getFoodDietaryTags,
  getFoodMealRequests,
} from 'features/food/foodSlice'
import {
  FoodMealRequestRouting,
  FoodMealRequestStatus,
  FoodMealRequestType,
  IFoodMealRequestResponse,
} from 'types/api/foodApi.interface'
import { formatDateForDisplay } from 'utils/helpers'
import { toast } from 'react-toastify'
import { ISelectValue } from 'constants/globalTypes'

const requestTypeOptions = [
  { label: 'Guest', value: FoodMealRequestType.Guest },
  { label: 'Emergency', value: FoodMealRequestType.Emergency },
]

const statusLabel = (status: number) => {
  if (status === FoodMealRequestStatus.Approved) return 'Approved'
  if (status === FoodMealRequestStatus.Rejected) return 'Rejected'
  if (status === FoodMealRequestStatus.Cancelled) return 'Cancelled'
  return 'Pending'
}

const statusPill = (status: number) => {
  if (status === FoodMealRequestStatus.Approved) return 'success'
  if (status === FoodMealRequestStatus.Rejected) return 'danger'
  if (status === FoodMealRequestStatus.Cancelled) return 'neutral'
  return 'accent'
}

const formatOperationalDay = (d: Date) => {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export const MealRequestsTab = () => {
  const dispatch = useDispatch<AppDispatch>()
  const { mealRequests, mealWindows, logisticsKitchens, dietaryTags, isLoading } = useSelector(
    (store: RootState) => store.food,
  )
  const [createOpen, setCreateOpen] = useState(false)
  const [deciding, setDeciding] = useState<IFoodMealRequestResponse | null>(null)
  const [decisionNote, setDecisionNote] = useState('')

  const [reqType, setReqType] = useState(FoodMealRequestType.Guest)
  const [dateFrom, setDateFrom] = useState<Date>(new Date())
  const [dateTo, setDateTo] = useState<Date>(new Date())
  const [windowDefId, setWindowDefId] = useState<number | null>(null)
  const [kitchenPreferenceId, setKitchenPreferenceId] = useState<number | null>(null)
  const [tagCodes, setTagCodes] = useState<string[]>([])
  const [qty, setQty] = useState('1')
  const [reason, setReason] = useState('')

  const refresh = () => {
    dispatch(getFoodMealRequests({ page: 1, limit: 50 }))
  }

  useEffect(() => {
    refresh()
    dispatch(getFoodDietaryTags({ page: 1, limit: 200 }))
  }, [dispatch])

  const windowOptions = (mealWindows.data || []).map((w) => ({ label: w.name, value: w.id }))
  const kitchenOptions = [
    { label: 'No preference', value: 0 },
    ...(logisticsKitchens.data || []).map((k) => ({ label: k.name, value: k.id })),
  ]

  const resetCreate = () => {
    setReqType(FoodMealRequestType.Guest)
    setDateFrom(new Date())
    setDateTo(new Date())
    setWindowDefId(null)
    setKitchenPreferenceId(null)
    setTagCodes([])
    setQty('1')
    setReason('')
  }

  const handleCreate = async () => {
    if (!windowDefId) {
      toast.warn('Meal window must be selected')
      return
    }
    if (!tagCodes.length) {
      toast.warn('Select at least one dietary tag')
      return
    }
    if (!Number(qty)) {
      toast.warn('Quantity must be at least 1')
      return
    }
    if (!reason.trim()) {
      toast.warn('Reason is required')
      return
    }
    const resp = await dispatch(
      createFoodMealRequest({
        type: reqType,
        dateFrom: formatOperationalDay(dateFrom),
        dateTo: formatOperationalDay(dateTo),
        windowDefId,
        lines: [{ dietaryTagCodes: tagCodes, qty: Number(qty) }],
        kitchenPreferenceId: kitchenPreferenceId || null,
        reason: reason.trim(),
      }),
    )
    if (resp.meta.requestStatus === 'fulfilled') {
      setCreateOpen(false)
      resetCreate()
      refresh()
    }
  }

  const handleDecide = async (approve: boolean) => {
    if (!deciding) return
    const resp = await dispatch(
      decideFoodMealRequest({
        id: deciding.id,
        body: { approve, decisionNote: decisionNote.trim() || null },
      }),
    )
    if (resp.meta.requestStatus === 'fulfilled') {
      setDeciding(null)
      setDecisionNote('')
      refresh()
    }
  }

  const columns: IHonestTableColumn<IFoodMealRequestResponse>[] = [
    { id: 'id', header: 'ID', cell: (row) => `#${row.id}` },
    {
      id: 'type',
      header: 'Type',
      cell: (row) => (row.type === FoodMealRequestType.Emergency ? 'Emergency' : 'Guest'),
    },
    {
      id: 'dates',
      header: 'Dates',
      cell: (row) =>
        row.dateFrom === row.dateTo
          ? formatDateForDisplay(row.dateFrom)
          : `${formatDateForDisplay(row.dateFrom)} – ${formatDateForDisplay(row.dateTo)}`,
    },
    { id: 'windowDef', header: 'Window', cell: (row) => row.windowDef?.name || '—' },
    {
      id: 'lines',
      header: 'Meals',
      minWidth: '14rem',
      cell: (row) => (
        <Flex gap='0.25rem' wrap>
          {(row.lines || []).map((line, idx) => (
            <StatusPill key={idx} status='accent'>
              {`${(line.dietaryTags || []).map((t) => t.code).join(' + ') || 'Untagged'} ×${line.qty}`}
            </StatusPill>
          ))}
        </Flex>
      ),
    },
    {
      id: 'routing',
      header: 'Routing',
      minWidth: '12rem',
      cell: (row) => (
        <Flex column>
          {(row.routingResultsPerDay || []).map((r, idx) => (
            <OtherText grey key={idx}>
              {`${formatDateForDisplay(r.operationalDay)}: ${r.kitchen?.name || '—'}${r.routing === FoodMealRequestRouting.Standby ? ' (standby)' : ''}`}
            </OtherText>
          ))}
        </Flex>
      ),
    },
    { id: 'reason', header: 'Reason', minWidth: '12rem', cell: (row) => row.reason },
    {
      id: 'status',
      header: 'Status',
      cell: (row) => (
        <Flex column>
          <StatusPill status={statusPill(row.status)}>{statusLabel(row.status)}</StatusPill>
          {row.decisionNote && <OtherText grey>{row.decisionNote}</OtherText>}
        </Flex>
      ),
    },
    {
      id: 'actions',
      header: '',
      align: 'right',
      cell: (row) =>
        row.status === FoodMealRequestStatus.Pending ? (
          <Button
            size='sm'
            variant='secondary'
            onClick={() => {
              setDeciding(row)
              setDecisionNote('')
            }}
          >
            Decide
          </Button>
        ) : null,
    },
  ]

  return (
    <Container>
      <ContainerHeader padding>
        <Flex between center width='100%' wrap gap='0.75rem'>
          <TitleText weight='700'>Meal Requests</TitleText>
          <Button variant='primary' onClick={() => setCreateOpen(true)}>
            New Request
          </Button>
        </Flex>
      </ContainerHeader>
      <ContainerContent>
        <HonestTable
          columns={columns}
          data={mealRequests.data || []}
          keyField='id'
          loading={isLoading}
          emptyMessage='No meal requests yet'
        />
      </ContainerContent>
      {createOpen && (
        <Modal black>
          <Flex column gap='1rem' maxHeight='75vh' overflowAuto>
            <Flex between width='100%' center>
              <TitleText weight='bold'>New Meal Request</TitleText>
              <CursorPointer onClick={() => setCreateOpen(false)}>
                <CloseIcon />
              </CursorPointer>
            </Flex>
            <Flex gap='1rem' wrap>
              <HonestSelectInput
                nomarg
                labelText='Type'
                placeholder='Select type…'
                options={requestTypeOptions}
                defaultValue={reqType}
                onChange={(v: ISelectValue) => setReqType(Number(v.value))}
                name='requestType'
              />
              <HonestSelectInput
                nomarg
                labelText='Meal window'
                placeholder='Select meal window…'
                options={windowOptions}
                defaultValue={windowDefId || undefined}
                onChange={(v: ISelectValue) => setWindowDefId(Number(v.value) || null)}
                name='requestWindow'
              />
              <HonestSelectInput
                nomarg
                labelText='Kitchen preference'
                placeholder='Select kitchen…'
                options={kitchenOptions}
                defaultValue={kitchenPreferenceId || 0}
                onChange={(v: ISelectValue) => setKitchenPreferenceId(Number(v.value) || null)}
                name='requestKitchen'
              />
            </Flex>
            <Flex gap='1rem' wrap>
              <CalendarInput
                nomarg
                labelText='From'
                defaultValue={dateFrom}
                onChange={(d: Date) => d && setDateFrom(d)}
              />
              <CalendarInput
                nomarg
                labelText='To'
                defaultValue={dateTo}
                onChange={(d: Date) => d && setDateTo(d)}
              />
              <Input
                labelText='Quantity'
                type='number'
                placeholder='e.g. 1'
                value={qty}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQty(e.target.value)}
                nomarg
              />
            </Flex>
            <Flex column gap='0.5rem'>
              <BodyText>Dietary Tags</BodyText>
              <Flex gap='0.5rem' wrap>
                {(dietaryTags.data || []).map((tag) => (
                  <CheckChip
                    key={tag.code}
                    text={tag.code}
                    active={tagCodes.includes(tag.code)}
                    onClick={() =>
                      setTagCodes((prev) =>
                        prev.includes(tag.code)
                          ? prev.filter((x) => x !== tag.code)
                          : [...prev, tag.code],
                      )
                    }
                  />
                ))}
              </Flex>
            </Flex>
            <Input
              labelText='Reason'
              value={reason}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setReason(e.target.value)}
              placeholder='Government inspection visit...'
              invalid={!reason.trim()}
              nomarg
            />
            <OtherText grey>
              Requests after the window&apos;s cut-off are served from standby only — check routing
              results after submitting.
            </OtherText>
            <Flex width='100%' center justcenter>
              <Button variant='primary' onClick={handleCreate}>
                Submit
              </Button>
            </Flex>
          </Flex>
        </Modal>
      )}
      {deciding && (
        <Modal black>
          <Flex column gap='1rem' maxHeight='75vh' overflowAuto>
            <Flex between width='100%' center>
              <TitleText weight='bold'>Decide Request #{deciding.id}</TitleText>
              <CursorPointer onClick={() => setDeciding(null)}>
                <CloseIcon />
              </CursorPointer>
            </Flex>
            <BodyText>{deciding.reason}</BodyText>
            <Input
              labelText='Decision note (optional)'
              value={decisionNote}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setDecisionNote(e.target.value)
              }
              placeholder='Approved, visitors arrive at 11:00...'
              nomarg
            />
            <Flex width='100%' gap='1rem' justcenter>
              <Button variant='primary' onClick={() => handleDecide(true)}>
                Approve
              </Button>
              <Button variant='secondary' onClick={() => handleDecide(false)}>
                Reject
              </Button>
            </Flex>
          </Flex>
        </Modal>
      )}
    </Container>
  )
}
